import { signInWithCustomToken } from 'firebase/auth'
import { useAuthStore } from '@/stores/auth'
import createLogger from '@/utils/logger'
import type { NavigationGuardNext, RouteLocationNormalizedGeneric } from 'vue-router'
import { auth, authReady } from '@/firebase'

const logger = createLogger('router/guards/customTokenAuth.ts')

/**
 * After custom-token sign-in, always continue at `/` so `redirects` can send staff to the
 * admin dashboard and customers to home — avoids briefly mounting `/account` as an admin
 * (which races staff-session heartbeat / claim hydration).
 */
export async function customTokenAuth(
  to: RouteLocationNormalizedGeneric,
  _from: RouteLocationNormalizedGeneric,
  next: NavigationGuardNext,
) {
  const token = to.query.token as string | undefined

  // If no token in query params, continue normally
  if (!token) {
    next()
    return
  }

  const authStore = useAuthStore()
  await authReady

  // If user is already authenticated, sign out then do custom token login
  if (authStore.isAuthenticated) {
    logger.log('User already authenticated, logging out and doing custom token login')
    await auth.signOut()
  }

  try {
    logger.log('Attempting to sign in with custom token')
    await signInWithCustomToken(auth, token)
    logger.log('Successfully signed in with custom token')

    // Hydrate role claims (and blocking-hook sessionClaims) before redirects run
    await authStore.untilLoadingIsDone()
    await authStore.untilRoleIsLoaded()

    next({ path: '/', replace: true })
  } catch (error) {
    logger.error('Error signing in with custom token:', error)
    next({ path: '/', replace: true })
  }
}
